"use client"
import { useState, cloneElement, useEffect, useRef } from 'react';
import { useFloating, useInteractions, useHover, useClick, useDismiss, FloatingPortal, FloatingTree, FloatingNode, useFloatingNodeId, useFloatingParentNodeId, Placement, shift, offset, safePolygon, flip, size, autoUpdate } from '@floating-ui/react';

// Keep the tooltip inside the viewport no matter how big its content is:
// - flip falls back to vertical placements only. shift() can only correct
//   along a placement's cross axis, so a side placement chosen for an
//   oversized tooltip (flip's bestFit does this on small screens) would hang
//   offscreen horizontally with no way to rescue it. Vertical placements are
//   always rescuable: shift fixes x, size() clamps height.
// - size() then caps the tooltip to the space actually available, so
//   oversized content scrolls (see the flex/overflow classes on the floating
//   element) instead of overflowing the screen.
// Callers that pass an explicit side placement still get it when it fits.
const VIEWPORT_PADDING = 8;
const keepOnscreenMiddleware = (maxWidth: number) => [
  offset(8),
  flip({
    fallbackPlacements: ['bottom-end', 'bottom', 'bottom-start', 'top-end', 'top', 'top-start'],
    fallbackStrategy: 'bestFit',
    padding: VIEWPORT_PADDING,
  }),
  shift({ padding: VIEWPORT_PADDING }),
  size({
    padding: VIEWPORT_PADDING,
    apply({ availableWidth, availableHeight, elements }) {
      Object.assign(elements.floating.style, {
        maxWidth: `${Math.floor(Math.min(maxWidth, availableWidth))}px`,
        maxHeight: `${Math.floor(availableHeight)}px`,
      });
    },
  }),
];

type TooltipProps = {
  children: React.ReactElement;
  content: React.ReactNode;
  placement?: Placement;
  interactive?: boolean;
  maxWidth?: number;
  forceOpen?: boolean;
  deactivate?: boolean;
  styleManually?: boolean;
  /** Also removes the default background and padding (not just the shadow),
   * so `content` can supply its own card chrome. */
  disableShadow?: boolean;
  zIndex?: number;
  /** Position the tooltip against this element instead of the hovered child
   * (hover/click interactions stay bound to the child). */
  positionRef?: React.RefObject<HTMLElement | null>;
};

const TooltipInner = ({
  children,
  content,
  placement = "top-start",
  maxWidth = 300,
  interactive = false,
  forceOpen = false,
  deactivate = false,
  styleManually = false,
  disableShadow = false,
  zIndex = 1000,
  positionRef,
}: TooltipProps) => {
  const [open, setOpen] = useState(forceOpen ? true : false);
  const [hoverEnabled, setHoverEnabled] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const lastMousePos = useRef<{x: number, y: number} | null>(null);
  const nodeId = useFloatingNodeId();
  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: open,
    onOpenChange: forceOpen ? undefined : (newOpen, _event, reason) => {
      // Scrolling disables hover until the mouse moves again — but only
      // hover-opens should respect that. Tap/click opens (mobile) must stay
      // allowed: touch scrolling never fires mousemove, so hoverEnabled
      // would otherwise stay false forever.
      if (newOpen && !hoverEnabled && reason === 'hover') return;
      setOpen(newOpen);
    },
    placement,
    whileElementsMounted: autoUpdate,
    middleware: keepOnscreenMiddleware(maxWidth),
  });
  const hover = useHover(context, {
    handleClose: interactive ? safePolygon() : undefined,
    enabled: hoverEnabled,
    // On mobile viewports useHover's touch path calls preventDefault on
    // touchstart, which blocks page scrolling when the gesture begins on a
    // tooltip-wrapped element. Restrict to mouse there.
    mouseOnly: isMobileViewport,
  });
  // With hover restricted to mouse on mobile, taps need their own way to
  // open the tooltip (and taps elsewhere to close it). ignoreMouse keeps
  // mouse input on the hover path even at mobile widths.
  const click = useClick(context, { enabled: isMobileViewport, ignoreMouse: true });
  const dismiss = useDismiss(context, { enabled: isMobileViewport });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
  ]);

  // Re-sync on each open in case the anchor element remounted between opens.
  useEffect(() => {
    if (positionRef) refs.setPositionReference(positionRef.current);
  }, [positionRef, refs, open]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobileViewport(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      // Scrolling within a tooltip card must not dismiss it; a wheel there
      // that moves the page instead still closes the tooltip via the page's
      // own scroll event. Matched by surface attribute rather than this
      // tooltip's own floating element because a nested tooltip's card is a
      // sibling portal, not a descendant — scrolling it must not close the
      // parent either.
      if (e.target instanceof Element && e.target.closest('[data-tooltip-surface="true"]')) return;
      setOpen(false);
      setHoverEnabled(false);
    };
    const handleMouseMove = (e: MouseEvent) => {
      const last = lastMousePos.current;
      if (last && (Math.abs(e.clientX - last.x) > 3 || Math.abs(e.clientY - last.y) > 3)) {
        setHoverEnabled(true);
      }
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('wheel', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const isClient = typeof window !== 'undefined';

  // Pass the child's own props through getReferenceProps so Floating UI
  // COMPOSES its event handlers with the child's instead of cloneElement
  // replacing them. Without this, on mobile viewports (where useClick is
  // enabled) the tooltip's injected onClick would overwrite a wrapped
  // button's onClick — taps would open the tooltip but never fire the
  // button's own action.
  const elementProps = {
    ref: refs.setReference,
    ...getReferenceProps(children.props),
  };

  const color = "#fffff8" //"rgba(240, 233, 211, 0.95)"
  return (
    <FloatingNode id={nodeId}>
      {cloneElement(children, elementProps)}
      {open && isClient && !deactivate && <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, maxWidth: `min(${maxWidth}px, 90vw)`, backgroundColor: disableShadow ? undefined : color, boxShadow: disableShadow ? undefined : '1px 1px 300px 0px rgba(10, 10, 0, 0.1), 1px 1px 3px 0px rgba(0, 0, 0, 0.2)', zIndex, touchAction: 'pan-y' }}
          {...getFloatingProps()}
          data-tooltip-surface="true"
          // flex-col so that when size() clamps maxHeight, a child that scrolls
          // (e.g. the footnote card) shrinks to fit and scrolls inside its own
          // chrome; overflow-y-auto is the fallback for non-scrollable content.
          className={`flex flex-col overflow-y-auto ${!styleManually ? `text-black border border-transparent rounded-sm ${disableShadow ? '' : 'p-2'} text-sm font-sans` : "p-2"}`}
        >
          {content}
        </div>
      </FloatingPortal>}
    </FloatingNode>
  );
};

// Tooltips can nest: a tooltip card's content may itself contain
// tooltip-wrapped elements (e.g. a link tooltip whose markdown contains more
// tooltipped links). Each tooltip portals to document.body, so without a
// shared FloatingTree the outer tooltip treats the inner tooltip's card as
// "outside" — moving the cursor onto the inner card would close the outer
// tooltip, unmounting the inner one's anchor and dismissing both. The tree
// (via the FloatingNode above) tells Floating UI's hover/safePolygon logic
// that an open child means the parent must stay open. The outermost tooltip
// creates the tree; nested ones join the ancestor's tree through context,
// which flows through the portal.
const Tooltip = (props: TooltipProps) => {
  const parentId = useFloatingParentNodeId();
  if (parentId === null) {
    return (
      <FloatingTree>
        <TooltipInner {...props} />
      </FloatingTree>
    );
  }
  return <TooltipInner {...props} />;
};

export const useScrollAwareHover = () => {
  const [hoverEnabled, setHoverEnabled] = useState(true);
  const lastMousePos = useRef<{x: number, y: number} | null>(null);
  useEffect(() => {
    const handleScroll = () => setHoverEnabled(false);
    const handleMouseMove = (e: MouseEvent) => {
      const last = lastMousePos.current;
      if (last && (Math.abs(e.clientX - last.x) > 3 || Math.abs(e.clientY - last.y) > 3)) {
        setHoverEnabled(true);
      }
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('wheel', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  return hoverEnabled;
};

export const useTooltip = ({placement, interactive}: {placement: Placement, interactive: boolean}) => {
  const [open, setOpen] = useState(false);
  const [hoverEnabled, setHoverEnabled] = useState(true);
  const lastMousePos = useRef<{x: number, y: number} | null>(null);
  const { refs, floatingStyles, context } = useFloating({
    open: open,
    onOpenChange: (newOpen) => {
      if (newOpen && !hoverEnabled) return;
      setOpen(newOpen);
    },
    placement,
    whileElementsMounted: autoUpdate,
    middleware: keepOnscreenMiddleware(Infinity),
  });
  const hover = useHover(context, {
    handleClose: interactive ? safePolygon() : undefined,
    enabled: hoverEnabled
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
      setHoverEnabled(false);
    };
    const handleMouseMove = (e: MouseEvent) => {
      const last = lastMousePos.current;
      if (last && (Math.abs(e.clientX - last.x) > 3 || Math.abs(e.clientY - last.y) > 3)) {
        setHoverEnabled(true);
      }
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('wheel', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return {
    ref: refs.setReference,
    floatingRef: refs.setFloating,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    open
  }
}

export default Tooltip;
